import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { revokeAdminUnlock } from "@/lib/prelaunch";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("sc_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        // Token is stale/expired/revoked. Clear the session AND the
        // prelaunch unlock flags so the next page load re-engages the
        // Coming Soon gate (critical for 2-hour demo sessions).
        localStorage.removeItem("sc_token");
        revokeAdminUnlock();
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (identifier, password) => {
    const r = await api.post("/auth/login", { identifier, password });
    localStorage.setItem("sc_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const login2fa = async (identifier, password, code) => {
    const r = await api.post("/auth/2fa/login", { identifier, password, code });
    localStorage.setItem("sc_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const signup = async (payload) => {
    const r = await api.post("/auth/signup", payload);
    localStorage.setItem("sc_token", r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };

  const logout = () => {
    localStorage.removeItem("sc_token");
    // Also clear the prelaunch unlock flags. For demo/buyer sessions this
    // is required (logging out must put them back on Coming Soon). For
    // admins it just means re-entering via the staff-login modal on the
    // next visit, which is the expected ergonomic.
    revokeAdminUnlock();
    setUser(null);
  };

  const refreshMe = async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch {}
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, login2fa, signup, logout, refreshMe }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
